import { Link, useNavigate } from 'react-router-dom';
import { useProject } from '../../data/queries/use-project';
import { useCaptures } from '../../data/queries/use-captures';
import { useSession } from '../auth/session';
import { ReportCaptureRow } from './ReportCaptureRow';
import { useReportBuilder } from './report-builder-state';
import { reportPhotosPath } from './report-route';
import { continueLabel } from './report-wording';

/**
 * STEP ONE — choose the Captures the report covers. M10 / S10-01, moved into a
 * route-backed step by S10-02.
 *
 * **CAPTURE-FIRST, AND THE SELECTION IS BRANDON'S** (PT §17, D-034). The list
 * is every Capture in this Project. Nothing is pre-selected, nothing is
 * pre-filtered, and **no status is excluded** — an OPEN Capture is real work and
 * may be reported on. No AI of any kind: no Sol, no provider call, no
 * "recommended work", and therefore no dependency on D-011.
 *
 * **THE SELECTION NOW LIVES IN THE LAYOUT ABOVE THIS STEP**, which is what lets
 * Brandon step forward to photos and come back — with Android's Back button —
 * and find his Captures still chosen. It is still memory only: leaving the
 * builder entirely discards it, and persistence is S10-05's job.
 *
 * **ORDER IS THE ORDER PROJECT HOME ALREADY SHOWS** — newest Capture number
 * first, straight from `useCaptures` with no filters. **That decides the
 * SELECTION LIST ONLY.** The order Captures appear in a finished report is a
 * separate, still-open product question, and nothing here settles it.
 */
export function ReportCaptureStep() {
  const builder = useReportBuilder();
  const navigate = useNavigate();
  const { project } = useProject(builder.projectId);
  const { profile } = useSession();
  const { status, captures, error } = useCaptures(builder.projectId);

  /**
   * **COUNTED AGAINST WHAT IS ACTUALLY ON SCREEN.** If a Capture ever left the
   * list while selected, counting the raw set would offer to continue with a
   * Capture Brandon can no longer see.
   */
  const selectedCount = captures.filter(({ capture }) => builder.captureIds.has(capture.id)).length;

  return (
    <main className="cl-screen report-screen">
      <p className="project-home-back">
        <Link className="cl-link" to={`/projects/${builder.projectId}`}>
          ‹ Project
        </Link>
      </p>

      <h1 className="cl-screen-title">Generate Report</h1>
      {/* **NEVER `project.name ?? project.id`.** A Project whose row has not
          arrived yet simply has no name line; a UUID would be worse than
          nothing, and it is the exact expression the no-internal-ids rule
          bans. */}
      {project ? <p className="report-project-name">{project.name}</p> : null}
      <p className="report-lead">Choose the Captures this report should cover.</p>

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading Captures…
        </p>
      ) : null}

      {status === 'failed' ? (
        <p className="cl-error" role="alert">
          {error ?? 'Could not load Captures. Check your connection and try again.'}
        </p>
      ) : null}

      {/* AN EMPTY PROJECT SAYS SO, AND OFFERS NO CONTINUE. The Captures list's
          own empty state points at a Capture button — which does not exist on
          this screen, so reusing its words would send him looking for a control
          that is not here. */}
      {status === 'ready' && captures.length === 0 ? (
        <p className="projects-empty-body">
          This Project has no Captures yet, so there is nothing to report on. Capture some work
          first, then come back.
        </p>
      ) : null}

      {status === 'ready' && captures.length > 0 ? (
        <>
          <ul className="report-capture-list">
            {captures.map(({ capture, organization }) => (
              <ReportCaptureRow
                key={capture.id}
                capture={capture}
                locationDisplay={organization?.location_display ?? null}
                timeZone={profile?.timezone ?? null}
                selected={builder.captureIds.has(capture.id)}
                onToggle={builder.toggleCapture}
              />
            ))}
          </ul>

          <button
            type="button"
            className="cl-button report-continue"
            disabled={selectedCount === 0}
            onClick={() => {
              void navigate(reportPhotosPath(builder.projectId));
            }}
          >
            {continueLabel(selectedCount)}
          </button>
        </>
      ) : null}
    </main>
  );
}
